import { zodResolver } from '@hookform/resolvers/zod';
import * as m from '@staiter/i18n';
import { PasswordResetRequest, type PasswordResetRequest as ReqType } from '@staiter/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '~/components/primitives/Button';
import { Input } from '~/components/primitives/Input';
import { requestPasswordReset } from '~/lib/api/auth';
import { ApiRequestError } from '~/lib/api/client';
import styles from './auth.password-reset.module.css';

export const Route = createFileRoute('/auth/password-reset')({
  component: ForgotPasswordPage,
});

function ForgotPasswordPage(): JSX.Element {
  const [submitted, setSubmitted] = useState(false);
  const [rateLimitSeconds, setRateLimitSeconds] = useState<number | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ReqType>({
    resolver: zodResolver(PasswordResetRequest),
    defaultValues: { email: '' },
  });

  const onSubmit = async (values: ReqType): Promise<void> => {
    try {
      await requestPasswordReset(values);
      setSubmitted(true);
    } catch (err) {
      // Backend всегда 200 (не раскрывает существование email), но 429 возможен.
      if (err instanceof ApiRequestError && err.status === 429 && err.retry_after) {
        setRateLimitSeconds(err.retry_after);
        startCountdown(err.retry_after, setRateLimitSeconds);
        return;
      }
      // Любая другая ошибка — всё равно показываем submitted state (anti-enumeration).
      setSubmitted(true);
    }
  };

  return (
    <main className={styles.page}>
      <section className={styles.card} aria-labelledby="forgot-pwd-title">
        <div className={styles.brand} aria-hidden="true">
          <img src="/favicon.svg" alt="" width={24} height={24} className={styles.brandLogo} />
          STAITER
        </div>
        <h1 id="forgot-pwd-title" className={styles.title}>
          {m.forgot_password_title()}
        </h1>
        <p className={styles.lede}>{m.forgot_password_lede()}</p>

        {submitted ? (
          <>
            <p className={styles.success} role="status">
              {m.forgot_password_sent()}
            </p>
            <a href="/login" className={styles.link}>
              {m.accept_invite_back_to_login()}
            </a>
          </>
        ) : (
          <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
            <Input
              label={m.login_email_label()}
              type="email"
              autoComplete="email"
              autoFocus
              error={errors.email?.message}
              {...register('email')}
            />

            {rateLimitSeconds !== null && (
              <p className={styles.rateLimit} role="status" aria-live="polite">
                {m.login_rate_limit({ seconds: rateLimitSeconds })}
              </p>
            )}

            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting || rateLimitSeconds !== null}
              loading={isSubmitting}
            >
              {m.forgot_password_submit()}
            </Button>

            <a href="/login" className={styles.link}>
              {m.accept_invite_back_to_login()}
            </a>
          </form>
        )}
      </section>
    </main>
  );
}

function startCountdown(initial: number, setter: (v: number | null) => void): void {
  let remaining = initial;
  const interval = setInterval(() => {
    remaining -= 1;
    if (remaining <= 0) {
      clearInterval(interval);
      setter(null);
    } else {
      setter(remaining);
    }
  }, 1000);
}
